import { action, thunk } from 'easy-peasy';
import * as externalAuthService from '../../services/externalAuth';
import {
  ExternalAuthConfig,
  ExternalAuthStatus,
  getDefaultExternalAuth,
} from '../../pages/externalAuth/types';

const externalAuthModel = {
  externalAuthList: [] as ExternalAuthConfig[],
  currentExternalAuth: null as ExternalAuthConfig | null,
  statusById: {} as Record<string, ExternalAuthStatus>,

  setExternalAuthList: action<any, ExternalAuthConfig[]>((state, payload) => {
    state.externalAuthList = payload;
  }),

  setCurrentExternalAuth: action<any, ExternalAuthConfig | null>(
    (state, payload) => {
      state.currentExternalAuth = payload;
    },
  ),

  setStatus: action<any, { id: string; status: ExternalAuthStatus }>(
    (state, { id, status }) => {
      state.statusById = { ...state.statusById, [id]: status };
    },
  ),

  getExternalAuthList: thunk(
    async (actions: any, { orgId }: { orgId: string }) => {
      try {
        const response = await externalAuthService.fetchExternalAuthList(orgId);
        actions.setExternalAuthList(
          response?.data?.data ?? response?.data ?? [],
        );
      } catch (error) {
        console.log('Error fetching external auth list', error);
        actions.setExternalAuthList([]);
      }
    },
  ),

  getExternalAuthById: thunk(
    async (actions: any, { orgId, id }: { orgId: string; id?: string }) => {
      if (!id) {
        actions.setCurrentExternalAuth(getDefaultExternalAuth());
        return;
      }
      try {
        const response = await externalAuthService.fetchExternalAuthById(
          orgId,
          id,
        );
        actions.setCurrentExternalAuth(
          response?.data?.data ?? response?.data ?? null,
        );
      } catch (error) {
        console.log('Error fetching external auth by id', error);
        actions.setCurrentExternalAuth(null);
      }
    },
  ),

  saveExternalAuth: thunk(
    async (actions: any, { orgId, data, onSuccess, commonAction }: any) => {
      try {
        const { id, ...payload } = data;
        const response = id
          ? await externalAuthService.updateExternalAuth(orgId, id, payload)
          : await externalAuthService.createExternalAuth(orgId, payload);
        commonAction.setSubmitSnackbar({
          status: true,
          messages: `External Auth ${data.name} successfully ${
            id ? 'updated' : 'created'
          }`,
        });
        if (onSuccess) onSuccess(response?.data?.data ?? response?.data);
        return response;
      } catch (error: any) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages:
            error?.response?.data?.error ||
            'Error saving external auth configuration',
          error: true,
        });
      }
    },
  ),

  deleteExternalAuth: thunk(
    async (actions: any, { orgId, id, commonAction }: any) => {
      try {
        await externalAuthService.deleteExternalAuth(orgId, id);
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'External Auth configuration successfully deleted',
        });
        actions.getExternalAuthList({ orgId });
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error deleting external auth configuration',
          error: true,
        });
      }
    },
  ),

  getExternalAuthStatus: thunk(
    async (actions: any, { orgId, id }: { orgId: string; id: string }) => {
      try {
        const response = await externalAuthService.fetchExternalAuthStatus(
          orgId,
          id,
        );
        const status: ExternalAuthStatus =
          response?.data?.data?.status ??
          response?.data?.status ??
          response?.data?.data ??
          response?.data;
        if (status) actions.setStatus({ id, status });
        return status;
      } catch (error) {
        console.log('Error fetching external auth status', error);
      }
    },
  ),

  authorizeExternalAuth: thunk(
    async (actions: any, { orgId, id, commonAction, onSuccess }: any) => {
      try {
        const response = await externalAuthService.fetchAuthorizeUrl(orgId, id);
        const url: string =
          response?.data?.data?.url ??
          response?.data?.url ??
          response?.data?.data ??
          response?.data;
        if (typeof url === 'string' && url && onSuccess) onSuccess(url);
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error getting authorization URL',
          error: true,
        });
      }
    },
  ),

  getExternalAuthToken: thunk(
    async (actions: any, { orgId, id, commonAction }: any) => {
      try {
        const response = await externalAuthService.fetchExternalAuthToken(
          orgId,
          id,
        );
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Token retrieved successfully',
        });
        actions.getExternalAuthStatus({ orgId, id });
        return response?.data?.data ?? response?.data;
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Failed to retrieve token',
          error: true,
        });
      }
    },
  ),

  uploadCertificate: thunk(
    async (actions: any, { orgId, id, file, commonAction }: any) => {
      try {
        const urlResponse = await externalAuthService.getCertificateUploadUrl(
          orgId,
          id,
        );
        const { url, fileName } =
          urlResponse?.data?.data ?? urlResponse?.data ?? {};

        await fetch(url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': 'application/x-pem-file' },
        });

        await externalAuthService.confirmCertificateUpload(orgId, id, fileName);

        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Certificate uploaded successfully',
        });
        actions.getExternalAuthList({ orgId });
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error uploading certificate',
          error: true,
        });
      }
    },
  ),
};

export default externalAuthModel;
